import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  Folder, File as FileIcon, Upload, FolderPlus, Download, Trash2,
  ChevronRight, ArrowLeft, RefreshCw, Cloud, CheckCircle2, Clock,
  AlertTriangle, HardDrive, X, FileImage, Film as FilmIcon, Music, Code, FileText,
  Monitor, Copy, Server, Layers
} from 'lucide-react';

const EXT_ICONS = {
  '.blend': Code, '.ma': Code, '.mb': Code, '.nk': Code,
  '.psd': FileImage, '.ai': FileImage, '.png': FileImage, '.jpg': FileImage, '.jpeg': FileImage, '.exr': FileImage,
  '.mov': FilmIcon, '.mp4': FilmIcon,
  '.wav': Music, '.mp3': Music,
  '.pdf': FileText, '.txt': FileText,
};
const EXT_COLORS = {
  '.blend': '#f59e0b', '.ma': '#06b6d4', '.nk': '#10b981',
  '.psd': '#3b82f6', '.png': '#8b5cf6', '.jpg': '#8b5cf6',
  '.mov': '#ec4899', '.mp4': '#ec4899', '.wav': '#14b8a6',
  '.pdf': '#ef4444',
};
const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB
const SMALL_MAX = 50 * 1024 * 1024; // 50 MB single-shot

export default function CloudStoragePage({ projectId, projectName }) {
  const { token } = useAuth();
  const api = useMemo(() => axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploads, setUploads] = useState([]); // in-flight uploads {id,name,progress,status}
  const [syncStatus, setSyncStatus] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [projectMeta, setProjectMeta] = useState(null);
  const [showMountModal, setShowMountModal] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/cloud/projects/${projectId}/tree?path=${encodeURIComponent(currentPath)}`);
      setItems(res.data.items || []);
      setBreadcrumbs(res.data.breadcrumbs || []);
    } catch (e) {
      setItems([]);
    }
    setLoading(false);
  }, [api, projectId, currentPath]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const res = await api.get(`/cloud/projects/${projectId}/sync-status`);
      setSyncStatus(res.data);
    } catch (e) { /* noop */ }
  }, [api, projectId]);

  useEffect(() => { loadTree(); loadSyncStatus(); }, [loadTree, loadSyncStatus]);
  useEffect(() => {
    api.get(`/projects/${projectId}`).then(r => setProjectMeta(r.data)).catch(() => {});
  }, [api, projectId]);
  useEffect(() => {
    const t = setInterval(loadSyncStatus, 15000);
    return () => clearInterval(t);
  }, [loadSyncStatus]);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await api.post(`/cloud/projects/${projectId}/folders`, { parent_path: currentPath, name });
      setNewFolderName('');
      setShowNewFolder(false);
      loadTree();
    } catch (e) {
      alert(e.response?.data?.detail || 'Create failed');
    }
  };

  const uploadFile = async (file, relativeParent = currentPath) => {
    const uploadId = `${Date.now()}-${file.name}-${Math.random()}`;
    setUploads(u => [...u, { id: uploadId, name: file.name, size: file.size, progress: 0, status: 'uploading' }]);
    try {
      if (file.size <= SMALL_MAX) {
        // Single-shot
        const fd = new FormData();
        fd.append('file', file);
        fd.append('parent_path', relativeParent);
        await api.post(`/cloud/projects/${projectId}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded * 100) / (e.total || file.size));
            setUploads(u => u.map(x => x.id === uploadId ? { ...x, progress: pct } : x));
          },
        });
      } else {
        // Multipart
        const initRes = await api.post(`/cloud/projects/${projectId}/upload/init`, {
          filename: file.name, size: file.size,
          content_type: file.type || 'application/octet-stream',
          parent_path: relativeParent,
        });
        const upId = initRes.data.upload_id;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);
          const fd = new FormData();
          fd.append('upload_id', upId);
          fd.append('chunk_index', i);
          fd.append('chunk', blob, file.name);
          await api.post(`/cloud/projects/${projectId}/upload/chunk`, fd);
          const pct = Math.round(((i + 1) * 100) / totalChunks);
          setUploads(u => u.map(x => x.id === uploadId ? { ...x, progress: pct } : x));
        }
        await api.post(`/cloud/projects/${projectId}/upload/complete`, { upload_id: upId });
      }
      setUploads(u => u.map(x => x.id === uploadId ? { ...x, progress: 100, status: 'done' } : x));
      setTimeout(() => setUploads(u => u.filter(x => x.id !== uploadId)), 2500);
      loadTree(); loadSyncStatus();
    } catch (e) {
      setUploads(u => u.map(x => x.id === uploadId ? { ...x, status: 'failed', error: e.message } : x));
    }
  };

  const uploadMany = async (files, basePath = currentPath) => {
    // files may contain webkitRelativePath when user picks a folder
    for (const f of files) {
      let parent = basePath;
      if (f.webkitRelativePath) {
        const parts = f.webkitRelativePath.split('/');
        parts.pop(); // drop filename
        const rel = parts.join('/');
        parent = basePath ? `${basePath}/${rel}` : rel;
        // Ensure intermediate folders exist
        let acc = basePath;
        for (const seg of parts) {
          acc = acc ? `${acc}/${seg}` : seg;
          try { await api.post(`/cloud/projects/${projectId}/folders`, { parent_path: acc.split('/').slice(0, -1).join('/'), name: seg }); } catch (e) { /* 409 ok */ }
        }
      }
      await uploadFile(f, parent);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    uploadMany(files);
  };

  const onPaste = useCallback((e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length > 0) {
      uploadMany(files);
    }
  }, [currentPath]); // eslint-disable-line

  useEffect(() => {
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onPaste]);

  const download = (item) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cloud/files/${item.id}/download?auth=${token}`;
    const a = document.createElement('a');
    a.href = url; a.download = item.name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"${item.is_dir ? ' and all contents' : ''}?`)) return;
    try {
      await api.delete(`/cloud/projects/${projectId}/files/${item.id}`);
      loadTree();
    } catch (e) { alert('Delete failed'); }
  };

  const syncNow = async () => {
    try {
      const res = await api.post(`/cloud/projects/${projectId}/sync-now`);
      alert(res.data.skipped
        ? 'Sync skipped: NAS bridge is offline.'
        : `Synced ${res.data.pushed_files || 0} files + ${res.data.pushed_dirs || 0} folders to NAS (agent: ${res.data.agent}).`);
      loadSyncStatus(); loadTree();
    } catch (e) { alert('Sync failed'); }
  };

  const createStructure = async () => {
    const rootName = prompt(
      "Root folder name (will appear as V:\\<name>\\...):",
      projectMeta?.cloud_folder_name || projectName?.replace(/\s+/g, '_') || 'Fairy_Tales'
    );
    if (!rootName) return;
    try {
      const res = await api.post(`/cloud/projects/${projectId}/create-structure`, { root_folder_name: rootName });
      alert(`${res.data.message}\n\nChapters included: ${res.data.chapters_count}\nCreated: ${res.data.created}\nAlready existed: ${res.data.skipped}\nTotal planned: ${res.data.total}`);
      loadTree(); loadSyncStatus();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const resetStructure = async () => {
    if (!window.confirm('⚠️  This SOFT-DELETES the entire cloud folder tree for this project.\n\nAll files and folders will be marked deleted (not permanent — still in DB).\n\nYou will then need to re-create the structure.\n\nProceed?')) return;
    if (!window.confirm('Are you ABSOLUTELY sure? Artists using WebDAV will immediately see an empty drive.')) return;
    try {
      const res = await api.delete(`/cloud/projects/${projectId}/reset-structure`);
      alert(`Reset complete. ${res.data.soft_deleted} items soft-deleted.`);
      loadTree(); loadSyncStatus();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const navigateTo = (path) => setCurrentPath(path);
  const navigateUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  // Show activation prompt if cloud isn't enabled yet for this project
  if (projectMeta && !projectMeta.use_cloud_storage) {
    return <EnableCloudCard projectId={projectId} projectName={projectName} api={api} onEnabled={() => {
      api.get(`/projects/${projectId}`).then(r => setProjectMeta(r.data)).catch(() => {});
      loadTree();
    }} />;
  }

  return (
    <div className="space-y-4" data-testid="cloud-storage"
         onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
         onDragLeave={() => setIsDragging(false)}
         onDrop={onDrop}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-sm flex items-center justify-center">
            <Cloud className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-white">Cloud Storage</h2>
            <p className="text-[10px] text-zinc-400">Work from anywhere — files auto-sync to NAS when bridge is online</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="create-structure-btn" onClick={createStructure}
            className="h-7 px-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-[10px] font-semibold rounded-sm flex items-center gap-1.5">
            <Layers className="w-3 h-3" /> Create Structure
          </button>
          <button data-testid="reset-structure-btn" onClick={resetStructure}
            className="h-7 px-3 bg-zinc-800 hover:bg-red-600 text-zinc-400 hover:text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <Trash2 className="w-3 h-3" /> Reset
          </button>
          <button data-testid="new-folder-btn" onClick={() => setShowNewFolder(true)}
            className="h-7 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <FolderPlus className="w-3 h-3" /> New Folder
          </button>
          <button data-testid="upload-files-btn" onClick={() => fileInputRef.current?.click()}
            className="h-7 px-3 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <Upload className="w-3 h-3" /> Upload Files
          </button>
          <button data-testid="upload-folder-btn" onClick={() => folderInputRef.current?.click()}
            className="h-7 px-3 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <FolderPlus className="w-3 h-3" /> Upload Folder
          </button>
          <button onClick={syncNow}
            className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> Sync to NAS
          </button>
          <button data-testid="mount-drive-btn" onClick={() => setShowMountModal(true)}
            className="h-7 px-3 bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-500 hover:to-violet-500 text-white text-[10px] font-medium rounded-sm flex items-center gap-1.5">
            <Monitor className="w-3 h-3" /> Mount as Drive
          </button>
        </div>
      </div>

      {/* Mount as Drive Modal */}
      {showMountModal && (
        <MountDriveModal projectMeta={projectMeta} onClose={() => setShowMountModal(false)} />
      )}

      {/* Sync status */}
      {syncStatus && (
        <div className="flex gap-3">
          <StatChip icon={CheckCircle2} label={`${syncStatus.synced} synced`} color="emerald" />
          <StatChip icon={Clock} label={`${syncStatus.pending} pending`} color={syncStatus.pending > 0 ? 'amber' : 'zinc'} />
          {syncStatus.failed > 0 && <StatChip icon={AlertTriangle} label={`${syncStatus.failed} failed`} color="red" />}
          <StatChip icon={HardDrive} label={`${syncStatus.total} total`} color="zinc" />
        </div>
      )}

      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/10 border-4 border-dashed border-blue-500 z-40 pointer-events-none flex items-center justify-center">
          <div className="bg-zinc-900 border border-blue-500 rounded-sm px-6 py-4">
            <Upload className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-sm text-white font-semibold">Drop to upload to "{currentPath || 'root'}"</p>
          </div>
        </div>
      )}

      {/* Breadcrumb + tree */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2">
          {currentPath && (
            <button onClick={navigateUp} data-testid="nav-up-btn" className="h-6 w-6 bg-zinc-800 hover:bg-zinc-700 rounded-sm flex items-center justify-center">
              <ArrowLeft className="w-3 h-3 text-zinc-400" />
            </button>
          )}
          <button onClick={() => navigateTo('')} className="text-[11px] text-zinc-400 hover:text-blue-400 font-medium">{projectName}</button>
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              <ChevronRight className="w-3 h-3 text-zinc-700" />
              <button onClick={() => navigateTo(b.path)} className="text-[11px] text-zinc-300 hover:text-blue-400">{b.name}</button>
            </React.Fragment>
          ))}
          <button onClick={loadTree} className="ml-auto h-6 w-6 bg-zinc-800 hover:bg-zinc-700 rounded-sm flex items-center justify-center">
            <RefreshCw className={`w-3 h-3 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="divide-y divide-zinc-800/40 max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center text-xs text-zinc-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Cloud className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
              <p className="text-sm text-zinc-400 mb-1">This folder is empty</p>
              <p className="text-[11px] text-zinc-600">Drop files here, paste from clipboard, or use the upload buttons</p>
            </div>
          ) : items.map(it => {
            const Icon = it.is_dir ? Folder : (EXT_ICONS[it.content_type?.split('/').pop()] || FileIcon);
            const color = it.is_dir ? '#f59e0b' : (EXT_COLORS[`.${it.name.split('.').pop()}`] || '#71717a');
            const syncBadge = it.sync_status === 'synced'
              ? { icon: CheckCircle2, text: 'synced', cls: 'text-emerald-500' }
              : it.sync_status === 'pending_to_nas'
                ? { icon: Clock, text: 'pending', cls: 'text-amber-500' }
                : { icon: AlertTriangle, text: 'failed', cls: 'text-red-500' };
            const SB = syncBadge.icon;
            return (
              <div key={it.id} data-testid={`cloud-item-${it.name}`} className="px-4 py-2 flex items-center gap-3 hover:bg-zinc-800/30 group">
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                {it.is_dir ? (
                  <button onClick={() => navigateTo(it.virtual_path)} className="flex-1 text-left text-xs text-zinc-100 hover:text-blue-400 font-medium">
                    {it.name}
                  </button>
                ) : (
                  <span className="flex-1 text-xs text-zinc-200">{it.name}</span>
                )}
                <span className="text-[9px] font-mono text-zinc-500 w-16 text-right">{it.size_human || ''}</span>
                {!it.is_dir && it.version > 1 && <span className="text-[9px] px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded-sm">v{it.version}</span>}
                <div className={`flex items-center gap-1 text-[10px] ${syncBadge.cls} w-20`}>
                  <SB className="w-3 h-3" /> {syncBadge.text}
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!it.is_dir && (
                    <button data-testid={`download-${it.name}`} onClick={() => download(it)}
                      className="h-6 w-6 bg-zinc-800 hover:bg-blue-600 rounded-sm flex items-center justify-center">
                      <Download className="w-3 h-3 text-zinc-300" />
                    </button>
                  )}
                  <button data-testid={`delete-${it.name}`} onClick={() => del(it)}
                    className="h-6 w-6 bg-zinc-800 hover:bg-red-600 rounded-sm flex items-center justify-center">
                    <Trash2 className="w-3 h-3 text-zinc-300" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* In-flight uploads */}
      {uploads.length > 0 && (
        <div className="fixed bottom-4 right-4 w-96 bg-zinc-900 border border-zinc-800 rounded-sm shadow-2xl p-3 z-50">
          <h4 className="text-xs font-semibold text-zinc-200 mb-2">Uploads ({uploads.length})</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {uploads.map(u => (
              <div key={u.id} className="text-[11px]">
                <div className="flex justify-between text-zinc-300"><span className="truncate">{u.name}</span><span>{u.progress}%</span></div>
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden mt-0.5">
                  <div className={`h-full ${u.status === 'failed' ? 'bg-red-500' : u.status === 'done' ? 'bg-emerald-500' : 'bg-blue-500'}`}
                    style={{ width: `${u.progress}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hidden inputs */}
      <input ref={fileInputRef} type="file" multiple className="hidden"
        onChange={e => { const files = Array.from(e.target.files || []); uploadMany(files); e.target.value = ''; }} />
      <input ref={folderInputRef} type="file" multiple webkitdirectory="" directory="" className="hidden"
        onChange={e => { const files = Array.from(e.target.files || []); uploadMany(files); e.target.value = ''; }} />

      {/* New folder modal */}
      {showNewFolder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowNewFolder(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5 w-80" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">New Folder in {currentPath || 'root'}</h3>
              <button onClick={() => setShowNewFolder(false)}><X className="w-4 h-4 text-zinc-500" /></button>
            </div>
            <input data-testid="new-folder-input" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createFolder()} autoFocus
              className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white"
              placeholder="Folder name" />
            <div className="flex gap-2 mt-3">
              <button data-testid="create-folder-submit" onClick={createFolder} disabled={!newFolderName.trim()}
                className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm disabled:opacity-40">Create</button>
              <button onClick={() => setShowNewFolder(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ icon: Icon, label, color }) {
  const map = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    zinc: 'bg-zinc-800/40 text-zinc-400 border-zinc-700',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-medium border ${map[color] || map.zinc}`}>
      <Icon className="w-3 h-3" /> {label}
    </div>
  );
}


function EnableCloudCard({ projectId, projectName, api, onEnabled }) {
  const defaultFolder = (projectName || 'Project').replace(/\s+/g, '_');
  const defaultSlug = defaultFolder.toLowerCase().replace(/[^a-z0-9]/g, '');
  const [folderName, setFolderName] = useState(defaultFolder);
  const [slug, setSlug] = useState(defaultSlug);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const enable = async () => {
    if (!folderName.trim() || !slug.trim()) {
      setError('Both fields are required.');
      return;
    }
    setBusy(true); setError('');
    try {
      await api.post(`/projects/${projectId}/enable-cloud`, {
        cloud_folder_name: folderName.trim(),
        cloud_slug: slug.trim().toLowerCase(),
      });
      onEnabled();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to enable cloud.');
      setBusy(false);
    }
  };

  return (
    <div data-testid="enable-cloud-card" className="max-w-2xl mx-auto mt-8">
      <div className="bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-violet-500/10 border border-cyan-500/20 rounded-sm p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-sm flex items-center justify-center flex-shrink-0">
            <Cloud className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-heading font-bold text-white mb-1">Enable Cloud Storage for {projectName}</h3>
            <p className="text-xs text-zinc-400 mb-5 leading-relaxed">
              Turn this project on for the cloud-first workflow. Artists worldwide can upload / download files via VawworldOS,
              sync to NAS runs automatically when the bridge is online, and you get a WebDAV drive mount at
              <code className="text-cyan-400 font-mono mx-1">/webdav/&lt;slug&gt;</code>. TSM and other projects stay unchanged.
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-[9px] text-zinc-400 uppercase tracking-wider">Cloud Folder Name *</label>
                <input data-testid="cloud-folder-name-input" value={folderName} onChange={e => setFolderName(e.target.value)}
                  className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono mt-1 focus:outline-none focus:border-cyan-500"
                  placeholder="Fairy_Tales" />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Must match what artists see on NAS. Artists will see <code className="text-cyan-400">V:\{folderName || 'Fairy_Tales'}\...</code> — identical path on both NAS and Cloud so Blender links resolve everywhere.
                </p>
              </div>
              <div>
                <label className="text-[9px] text-zinc-400 uppercase tracking-wider">WebDAV Slug (lowercase, no spaces) *</label>
                <input data-testid="cloud-slug-input" value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white font-mono mt-1 focus:outline-none focus:border-cyan-500"
                  placeholder="fairytales" />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Mount URL will be: <code className="text-cyan-400">{process.env.REACT_APP_BACKEND_URL}/webdav/{slug || 'fairytales'}</code>
                </p>
              </div>
            </div>

            {error && (
              <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-sm text-[11px] text-red-300 mb-3">{error}</div>
            )}

            <div className="flex gap-2">
              <button data-testid="enable-cloud-submit" onClick={enable} disabled={busy || !folderName || !slug}
                className="flex-1 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold rounded-sm disabled:opacity-40 flex items-center justify-center gap-2">
                <Cloud className="w-4 h-4" /> {busy ? 'Enabling...' : 'Enable Cloud Storage'}
              </button>
            </div>

            <div className="mt-5 pt-4 border-t border-zinc-800/60 text-[10px] text-zinc-500 space-y-1">
              <p className="font-semibold text-zinc-400 mb-1">What happens when you click Enable:</p>
              <p>1. Project is flagged <code className="text-cyan-400">use_cloud_storage=true</code></p>
              <p>2. Cloud tab becomes active — drag-drop, uploads, folder tree</p>
              <p>3. Cloud ↔ NAS sync kicks in every 30 s</p>
              <p>4. WebDAV path <code className="text-cyan-400">/webdav/{slug || 'fairytales'}</code> is live for drive mounting (once Emergent whitelists WebDAV methods)</p>
              <p>5. You can then click <b>"Create Structure"</b> to auto-build the Blender Studio folder tree</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function MountDriveModal({ projectMeta, onClose }) {  const [copied, setCopied] = useState('');
  const slug = projectMeta?.cloud_slug || 'fairytales';
  const folder = projectMeta?.cloud_folder_name || 'Fairy_Tales';
  const host = (process.env.REACT_APP_BACKEND_URL || '').replace(/^https?:\/\//, '');
  const webdavUrl = `${process.env.REACT_APP_BACKEND_URL}/webdav/${slug}`;
  const fullPath = `${webdavUrl}/${folder}`;
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="mount-drive-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm w-[52rem] max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-fuchsia-500 to-violet-600 rounded-sm flex items-center justify-center">
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-heading font-bold text-white">Mount VawworldOS Cloud as Drive</h3>
              <p className="text-[11px] text-zinc-400">Same folder structure as NAS. Artists see exactly <code className="text-cyan-400">V:\{folder}\Hensal&Gretel\...</code> on both.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5 text-sm">
          {/* URL box */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
            <label className="text-[9px] text-zinc-500 uppercase tracking-wider">WebDAV URL for this project</label>
            <div className="flex items-center gap-2 mt-2">
              <code data-testid="webdav-url" className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-sm text-cyan-400 font-mono text-xs overflow-x-auto">{webdavUrl}</code>
              <button onClick={() => copy(webdavUrl, 'url')}
                className={`h-9 px-4 text-xs font-medium rounded-sm flex items-center gap-1.5 ${copied === 'url' ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                <Copy className="w-3 h-3" /> {copied === 'url' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">Artist login = their VawworldOS email + password</p>
          </div>

          {/* Windows */}
          <div>
            <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5"><Server className="w-3 h-3 text-blue-400" /> Windows 10 / 11 — Map Network Drive</h4>
            <ol className="space-y-2 text-[11px] text-zinc-300 list-decimal pl-5">
              <li>Open <b>File Explorer</b> → Right-click <b>"This PC"</b> → <b>Map network drive...</b></li>
              <li>Drive letter: <code className="bg-zinc-800 px-1 rounded text-cyan-400">V:</code> (same letter you use for NAS — Blender link paths stay identical)</li>
              <li>Folder: paste <code className="bg-zinc-800 px-1 rounded text-cyan-400">{webdavUrl}</code></li>
              <li>Check <b>"Connect using different credentials"</b> → Click Finish</li>
              <li>Enter your <b>VawworldOS email</b> as username, your <b>VawworldOS password</b> as password. Check "Remember my credentials".</li>
              <li>Done. Open Blender → File → Open → <code className="bg-zinc-800 px-1 rounded text-cyan-400">V:\{folder}\Hensal&Gretel\03_Production\shot_010.blend</code></li>
            </ol>
          </div>

          {/* macOS */}
          <div>
            <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5"><Server className="w-3 h-3 text-zinc-400" /> macOS — Finder Connect to Server</h4>
            <ol className="space-y-2 text-[11px] text-zinc-300 list-decimal pl-5">
              <li>Finder → <b>Go</b> → <b>Connect to Server...</b> (⌘K)</li>
              <li>Enter <code className="bg-zinc-800 px-1 rounded text-cyan-400">{webdavUrl}</code></li>
              <li>Registered User → VawworldOS email + password</li>
              <li>Mount appears in Finder sidebar as <b>{slug}</b>. Open Blender from there.</li>
            </ol>
          </div>

          {/* Linux */}
          <div>
            <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5"><Server className="w-3 h-3 text-amber-400" /> Linux — gvfs / davfs2</h4>
            <code className="block bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-[10px] font-mono text-cyan-400">
              # Nautilus (GNOME):  {webdavUrl}{'\n'}
              # davfs2 CLI:       sudo mount -t davfs {webdavUrl} /mnt/vos
            </code>
          </div>

          {/* rclone power mode */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
            <h4 className="text-xs font-semibold text-white mb-2">Power mode — rclone (recommended for &gt;1GB files)</h4>
            <p className="text-[11px] text-zinc-400 mb-2">Faster, built-in caching, survives network blips. One-time setup per artist.</p>
            <code className="block bg-zinc-900 rounded-sm px-3 py-2 text-[10px] font-mono text-cyan-400 whitespace-pre">
              rclone config{'\n'}
              type: webdav · url: {webdavUrl} · vendor: other · user: [VawworldOS email] · pass: [VawworldOS password]{'\n'}
              rclone mount vos: V: --vfs-cache-mode full
            </code>
          </div>

          {/* Deployment note */}
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3">
            <p className="text-[11px] text-amber-300 font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Deployment note</p>
            <p className="text-[10px] text-amber-200/80 leading-relaxed">
              The preview URL (this environment) goes through Cloudflare which blocks WebDAV methods (PROPFIND, MKCOL). For production
              you'll need to either (a) expose WebDAV on a subdomain that bypasses the WAF, or (b) whitelist WebDAV methods in the
              Cloudflare rule set, or (c) artists connect via VPN to the backend directly. Ask your infra to enable once you go live.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
